/**
 * Update store — single source of truth for the self-update flow.
 *
 * Responsibilities:
 *   - Hold the current update status (idle | checking | up_to_date | …).
 *   - Drive a periodic background check (default: every hour).
 *   - Expose `checkForUpdate()` for on-demand checks (settings button).
 *   - Drive the download + install via Tauri commands and forward
 *     `update:progress` / `update:complete` events into the state.
 *
 * Not persisted — update status is ephemeral and re-derived on every launch.
 */

import { create } from 'zustand'
import type { UnlistenFn } from '@tauri-apps/api/event'
import { IS_TAURI } from '@/lib/platform'

// ── Public types ─────────────────────────────────────────────────────────────

export type UpdateStatus =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'up_to_date'; version: string; checkedAt: number }
  | { kind: 'needs_reauth' }
  | {
      kind: 'update_available'
      currentVersion: string
      latestVersion: string
      fileId: string
      fileName: string
      fileSizeBytes: number | null
    }
  | {
      kind: 'downloading'
      latestVersion: string
      bytesDownloaded: number
      totalBytes: number
    }
  | { kind: 'installing'; latestVersion: string }
  | { kind: 'ready'; latestVersion: string }
  | { kind: 'error'; message: string }

interface UpdateCheckResult {
  current_version: string
  latest_version: string | null
  file_id: string | null
  file_name: string | null
  file_size_bytes: number | null
  update_available: boolean
  needs_reauth: boolean
}

interface ProgressPayload {
  bytes_downloaded: number
  total_bytes: number
}

// ── Constants ────────────────────────────────────────────────────────────────

/** How often the background poll runs. The user explicitly requested hourly. */
const POLL_INTERVAL_MS = 60 * 60 * 1000 // 1 hour

// ── Store ────────────────────────────────────────────────────────────────────

interface UpdateState {
  status: UpdateStatus

  // Internal lifecycle handles — not consumed by UI components.
  _pollIntervalId: number | null
  _progressUnlisten: UnlistenFn | null
  _installingUnlisten: UnlistenFn | null
  _completeUnlisten: UnlistenFn | null

  // Public actions
  checkForUpdate: () => Promise<void>
  downloadAndInstall: () => Promise<void>
  startPolling: () => void
  stopPolling: () => void
  /** Reset to idle (e.g. user dismisses the badge). */
  dismiss: () => void
}

async function stopListeners(): Promise<void> {
  const { _progressUnlisten, _installingUnlisten, _completeUnlisten } =
    useUpdateStore.getState()
  if (_progressUnlisten) _progressUnlisten()
  if (_installingUnlisten) _installingUnlisten()
  if (_completeUnlisten) _completeUnlisten()
  useUpdateStore.setState({
    _progressUnlisten: null,
    _installingUnlisten: null,
    _completeUnlisten: null,
  })
}

export const useUpdateStore = create<UpdateState>((set, get) => ({
  status: { kind: 'idle' },
  _pollIntervalId: null,
  _progressUnlisten: null,
  _installingUnlisten: null,
  _completeUnlisten: null,

  // ── check ────────────────────────────────────────────────────────────────
  checkForUpdate: async () => {
    // In-app auto-update replaces the signed .app bundle on disk — a desktop
    // concept with no browser counterpart, where a deploy simply serves new
    // assets. Returning early leaves `status` at 'idle', which every consumer
    // already renders as "nothing to show" (UpdateIndicator returns null,
    // UpdatesSection shows the up-to-date state). Task 4093.
    if (!IS_TAURI) return

    // Don't clobber an in-progress download/install with a "checking" state.
    const current = get().status
    if (
      current.kind === 'downloading' ||
      current.kind === 'installing' ||
      current.kind === 'ready'
    ) {
      return
    }

    set({ status: { kind: 'checking' } })
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      const result = await invoke<UpdateCheckResult>('check_for_update')
      if (result.needs_reauth) {
        set({ status: { kind: 'needs_reauth' } })
        return
      }
      if (
        result.update_available &&
        result.latest_version &&
        result.file_id &&
        result.file_name
      ) {
        set({
          status: {
            kind: 'update_available',
            currentVersion: result.current_version,
            latestVersion: result.latest_version,
            fileId: result.file_id,
            fileName: result.file_name,
            fileSizeBytes: result.file_size_bytes,
          },
        })
        return
      }
      set({
        status: {
          kind: 'up_to_date',
          version: result.current_version,
          checkedAt: Date.now(),
        },
      })
    } catch (err) {
      set({ status: { kind: 'error', message: String(err) } })
    }
  },

  // ── download + install ───────────────────────────────────────────────────
  downloadAndInstall: async () => {
    // Guard FIRST, before any state mutation. Unreachable in the browser today
    // (this action requires status 'update_available', which only
    // checkForUpdate sets and which returns early there) — but the guard used
    // to sit after `set({ kind: 'downloading' })` and `await stopListeners()`,
    // so if it were ever reached the store would strand at 'downloading' with
    // no listeners. Defence in depth, and it costs nothing. It also remains
    // what lets Rollup drop the Tauri imports below.
    if (!IS_TAURI) return

    const current = get().status
    if (current.kind !== 'update_available') {
      // Only valid from update_available — the UI gates this action.
      return
    }
    const { fileId, fileName, latestVersion } = current

    set({
      status: {
        kind: 'downloading',
        latestVersion,
        bytesDownloaded: 0,
        totalBytes: current.fileSizeBytes ?? 0,
      },
    })

    // Subscribe to progress and completion events for the duration of the
    // download. Clean up previous listeners first if any are dangling.
    await stopListeners()

    const { invoke } = await import('@tauri-apps/api/core')
    const { listen } = await import('@tauri-apps/api/event')

    const progressUnlisten = await listen<ProgressPayload>(
      'update:progress',
      event => {
        const prev = get().status
        if (prev.kind !== 'downloading') return
        set({
          status: {
            ...prev,
            bytesDownloaded: event.payload.bytes_downloaded,
            totalBytes: event.payload.total_bytes || prev.totalBytes,
          },
        })
      },
    )
    // The Rust side emits `update:installing` after the download completes
    // and right before the bundle swap. This is what powers the "Installing…"
    // label in the indicator (during which the macOS admin prompt is shown).
    const installingUnlisten = await listen('update:installing', () => {
      set({ status: { kind: 'installing', latestVersion } })
    })
    const completeUnlisten = await listen('update:complete', () => {
      set({ status: { kind: 'ready', latestVersion } })
    })
    set({
      _progressUnlisten: progressUnlisten,
      _installingUnlisten: installingUnlisten,
      _completeUnlisten: completeUnlisten,
    })

    try {
      await invoke('download_and_install_update', { fileId, fileName })
      // Fallback if the `update:complete` event hasn't already fired (race).
      const finalStatus = get().status
      if (
        finalStatus.kind === 'downloading' ||
        finalStatus.kind === 'installing'
      ) {
        set({ status: { kind: 'ready', latestVersion } })
      }
    } catch (err) {
      set({ status: { kind: 'error', message: String(err) } })
    } finally {
      await stopListeners()
    }
  },

  // ── polling lifecycle ────────────────────────────────────────────────────
  startPolling: () => {
    // No updater in the browser — skip the hourly timer entirely rather than
    // run an interval whose every tick returns early.
    if (!IS_TAURI) return

    const { _pollIntervalId } = get()
    if (_pollIntervalId !== null) return // already polling

    // Initial check on start — the operator sees an "update available" badge
    // promptly without waiting an hour. checkForUpdate handles its own state.
    void get().checkForUpdate()

    const intervalId = window.setInterval(() => {
      void get().checkForUpdate()
    }, POLL_INTERVAL_MS)
    set({ _pollIntervalId: intervalId })
  },

  stopPolling: () => {
    const { _pollIntervalId } = get()
    if (_pollIntervalId !== null) {
      window.clearInterval(_pollIntervalId)
      set({ _pollIntervalId: null })
    }
    // Also drop any in-flight event listeners — fire-and-forget.
    void stopListeners()
  },

  dismiss: () => {
    set({ status: { kind: 'idle' } })
  },
}))
